import React, { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import { archiveContacts, unarchiveContacts } from 'api/contacts/single';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ArchiveContactsProps {
    isArchived: boolean;
    contacts: Contact[];
    children?: React.ReactNode;
    onSuccess?: () => void;
}

const ArchiveContacts = ({ isArchived, contacts, onSuccess, children }: ArchiveContactsProps) => {
    const toast = useToast();
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();

    const contactsIds = useMemo(() => contacts.map(item => item.id), [contacts]);

    const { mutate, isPending: isLoading } = useMutation({
        mutationFn: () => {
            const handler = isArchived ? unarchiveContacts : archiveContacts;
            return handler({ accountId, contactsIds });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });
            toast.success(`Contacts ${isArchived ? 'unarchived' : 'archived'} successfully`);
            forceRender();
            onSuccess?.();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(<>{error.response?.data.message ?? 'Unknown error occurred'}</>);
        },
    });

    return (
        <AlertDialog.Root key={key}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Title>
                            {isArchived ? 'Unarchive Contacts' : 'Archive Contacts'}
                        </AlertDialog.Title>
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        {isLoading && <Loader />}
                        <AlertDialog.Description>
                            You are about to {isArchived ? 'unarchive' : 'archive'}{' '}
                            <span>{contacts.length} selected</span>{' '}
                            {pluralize(contacts.length, 'contact')}.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <Button variant="outline" color="neutral" onClick={forceRender}>
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            onClick={() => mutate()}
                            disabled={contacts.length === 0}
                        >
                            {isArchived ? 'Unarchive' : 'Archive'} {contacts.length}{' '}
                            {pluralize(contacts.length, 'contact')}
                        </Button>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const ArchiveContactsTrigger = AlertDialog.Trigger;

const Root = ArchiveContacts;
const Trigger = ArchiveContactsTrigger;

export {
    Root,
    Trigger,
    //
    ArchiveContacts,
    ArchiveContactsTrigger,
};
